import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGavel,
  faCube,
  faCoins,
  faUser,
  faRoute,
  faMapMarkedAlt,
  faBus,
  faCompass,
  faArrowRight,
  faArrowLeft,
  faWallet,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { useUserData } from '../App';
import ThemeToggle from '../components/ThemeToggle';

const BiddingDisplaySoftware = () => {
  const navigate = useNavigate();
  const { userData, updateUserData } = useUserData();
  const [sortBy, setSortBy] = useState('price');
  const [selectedBid, setSelectedBid] = useState(null);

  // 任务信息 - 软件开发领域
  const taskInfo = {
    title: '毕设系统开发服务',
    description: ' 为本科生和研究生提供专业的毕业设计系统开发服务，包含源码和完整文档',
    requirements: ' 源码交付/详细文档/技术支持/按时完成/答辩指导',
    publishTime: '2025-07-15 10:30',
    deadline: '2025-07-22 10:30',
    totalBids: 4,
    icon: faRoute
  };

  // 竞价方案数据 - 软件开发领域
  const biddingData = [
    {
      id: 1,
      providerName: '技术软件',
      icon: faRoute,
      successRate: 92,
      responseTime: '30分钟前',
      price: 1998,
      services: [
        '基础开发: 基础功能实现，简单数据库设计',
        '源码交付: 完整源代码，详细技术文档',
        '部署指导: 系统部署与安装教程',
        '技术支持: 答辩前技术咨询支持',
        '简易界面: 基础UI设计，功能完整',
        '答辩支持: 代码结构合理，便于展示',
        '修改服务: 提供基础系统修改服务'
      ]
    },
    {
      id: 2,
      providerName: '智程开发',
      icon: faMapMarkedAlt,
      successRate: 85,
      responseTime: '2小时前',
      price: 2998,
      services: [
        '全栈开发: 全功能系统开发，完整数据库设计',
        '完整交付: 源码+注释，开发文档和说明书',
        '辅导讲解: 一对一辅导，系统功能讲解',
        '答辩准备: 系统演示视频录制，答辩PPT制作',
        '高级UI: 精美UI设计，响应式界面',
        '全程支持: 全程跟进，提供即时技术咨询'
      ]
    },
    {
      id: 3,
      providerName: '程序猿工作室',
      icon: faBus,
      successRate: 78,
      responseTime: '5小时前',
      price: 998,
      services: [
        '基础功能: 基础功能实现，简易数据库',
        '源码交付: 源码交付，简易说明文档',
        '经济实惠: 性价比高，预算有限的首选',
        '快速交付: 快速交付',
        '简洁UI: 简洁界面，功能为主',
        '基础支持: 提供基础邮件支持'
      ]
    },
    {
      id: 4,
      providerName: '星辰科技',
      icon: faCompass,
      successRate: 95,
      responseTime: '15分钟前',
      price: 3998,
      services: [
        '高级开发: 高级功能，高并发架构，完整数据库',
        '全套文档: 源码+注释+文档+操作手册',
        '论文指导: 论文指导服务，提供论文参考',
        '定制开发: 完成高度定制化开发',
        '专业培训: 提供定制化系统讲解培训',
        '专属顾问: 专属技术顾问，全程跟进',
        '扩展接口: 可接入第三方API，扩展性强'
      ]
    }
  ];

  // 排序逻辑
  const getSortedBids = () => {
    const sorted = [...biddingData];
    switch (sortBy) {
      case 'price':
        return sorted.sort((a, b) => a.price - b.price);
      case 'successRate':
        return sorted.sort((a, b) => b.successRate - a.successRate);
      case 'responseTime':
        return sorted.sort((a, b) => {
          const timeA = a.responseTime.includes('分钟') ? parseInt(a.responseTime) : parseInt(a.responseTime) * 60;
          const timeB = b.responseTime.includes('分钟') ? parseInt(b.responseTime) : parseInt(b.responseTime) * 60;
          return timeA - timeB;
        });
      default:
        return sorted;
    }
  };

  const handleSelectBid = (bidId) => {
    setSelectedBid(bidId);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleConnectWallet = () => {
    updateUserData({ isWalletConnected: !userData.isWalletConnected });
  };

  const handleConfirmSelection = () => {
    if (selectedBid) {
      // 获取选中的竞价数据
      const selectedBidData = biddingData.find(bid => bid.id === selectedBid);
      
      // 跳转到支付方式选择页面，传递选中的竞价信息和领域信息
      navigate('/payment-method', {
        state: { 
          selectedBid: selectedBidData,
          domain: 'enterprise',
          taskInfo: taskInfo
        }
      });
    }
  };

  return (
    <div className="w-full min-h-screen bg-white dark:bg-mystic-900 font-pixel transition-colors duration-300">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b-2 border-black dark:border-gray-400 bg-white dark:bg-mystic-800 transition-colors duration-300">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          {/* Logo and Back */}
          <div className="flex items-center space-x-6">
            <button 
              onClick={handleBack}
              className="back-button flex items-center space-x-2 bg-white dark:bg-mystic-700 text-black dark:text-white border-2 border-black dark:border-gray-400 px-4 py-2 transition-colors duration-300 hover:-translate-x-px hover:-translate-y-px"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>返回</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-black dark:bg-mystic-600 flex items-center justify-center transition-colors duration-300">
                <FontAwesomeIcon icon={faCube} className="text-white text-sm" />
              </div>
              <h1 className="pixel-title text-xl text-black dark:text-white transition-colors duration-300">POLYAGENT MARKET</h1>
            </div>
          </div>
          
          {/* User Info and Wallet */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <ThemeToggle />
            {/* User Avatar */}
            <div className="flex items-center space-x-2">
              <span className="text-xl">{userData.avatar}</span>
              <span className="text-sm font-bold text-black dark:text-white transition-colors duration-300">{userData.username}</span>
            </div>
            
            {/* Wallet Status */}
            <button 
              onClick={handleConnectWallet}
              className={`px-4 py-2 flex items-center space-x-2 border-2 border-black dark:border-gray-400 font-bold transition-colors duration-300 ${
                userData.isWalletConnected 
                  ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-700' 
                  : 'bg-white dark:bg-mystic-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-mystic-600'
              }`}
            >
              <FontAwesomeIcon icon={faWallet} />
              <span>{userData.isWalletConnected ? '钱包已连接' : '连接钱包'}</span>
              {userData.isWalletConnected && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 ml-1" />
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="w-full p-6">
        {/* Task Summary */}
        <div className="pixel-card w-full mb-8 bg-white dark:bg-mystic-800 transition-colors duration-300">
          <div className="flex items-center h-12 px-4 bg-black dark:bg-mystic-700 text-white border-b-2 border-black dark:border-gray-400 transition-colors duration-300">
            <h2 className="pixel-title text-base font-bold uppercase text-white">任务概要</h2>
          </div>
          <div className="flex justify-between items-center p-4">
            <div className="flex items-start">
              <div className="pixel-border w-16 h-16 flex items-center justify-center border-2 border-black dark:border-gray-400 mr-4 bg-white dark:bg-mystic-700 transition-colors duration-300">
                <FontAwesomeIcon icon={taskInfo.icon} className="text-2xl text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="text-black dark:text-white transition-colors duration-300">
                <h3 className="mb-2 text-base font-bold">{taskInfo.title}</h3>
                <p className="mb-1 text-sm">
                  <span className="font-bold">描述:</span>
                  <span> {taskInfo.description}</span>
                </p>
                <p className="text-sm">
                  <span className="font-bold">要求:</span>
                  <span> {taskInfo.requirements}</span>
                </p>
              </div>
            </div>
            <div className="border-l-2 border-black dark:border-gray-400 pl-4 text-black dark:text-white transition-colors duration-300">
              <p className="text-sm">
                <span className="font-bold">发布时间:</span>
                <span> {taskInfo.publishTime}</span>
              </p>
              <p className="text-sm">
                <span className="font-bold">竞价截止:</span>
                <span> {taskInfo.deadline}</span>
              </p>
              <p className="mt-2 text-sm font-bold text-gray-700 dark:text-gray-300">
                共收到 {taskInfo.totalBids} 个竞价方案
              </p>
            </div>
          </div>
        </div>

        {/* Sort and Status */}
        <div className="flex justify-between w-full mb-6">
          <div className="pixel-card flex bg-white dark:bg-mystic-800 transition-colors duration-300">
            <div className="border-r-2 border-black dark:border-gray-400 flex items-center h-10 px-4 bg-black dark:bg-mystic-700 text-white transition-colors duration-300">
              <span className="text-sm font-bold">排序方式</span>
            </div>
            <div className="flex">
              <button
                onClick={() => setSortBy('price')}
                className={`sorting-option flex items-center h-10 px-4 ${
                  sortBy === 'price' ? 'active' : ''
                }`}
              >
                <span className="text-sm font-bold">价格 ↑</span>
              </button>
              <button
                onClick={() => setSortBy('successRate')}
                className={`sorting-option border-l border-black dark:border-gray-400 flex items-center h-10 px-4 ${
                  sortBy === 'successRate' ? 'active' : ''
                }`}
              >
                <span className="text-sm font-bold">成功率 ↓</span>
              </button>
              <button
                onClick={() => setSortBy('responseTime')}
                className={`sorting-option border-l border-black dark:border-gray-400 flex items-center h-10 px-4 ${
                  sortBy === 'responseTime' ? 'active' : ''
                }`}
              >
                <span className="text-sm font-bold">响应时间 ↑</span>
              </button>
            </div>
          </div>
          
          <div className="pixel-card flex items-center h-10 px-4 bg-black dark:bg-mystic-700 text-white transition-colors duration-300">
            <span className="text-sm">竞价中: 2天11小时后截止</span>
            <div className="relative w-4 h-4 ml-2 bg-white dark:bg-gray-200 transition-colors duration-300">
              <div className="absolute top-1 left-1 w-2 h-2 bg-black dark:bg-mystic-800 transition-colors duration-300"></div>
            </div>
          </div>
        </div>

        {/* Scrollable Bidding List */}
        <div className="w-full max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-black scrollbar-track-gray-200">
          {getSortedBids().map((bid) => (
            <div
              key={bid.id}
              className={`bidding-card pixel-card flex w-full mb-6 bg-white dark:bg-mystic-800 transition-colors duration-300 ${
                selectedBid === bid.id ? 'ring-4 ring-indigo-500' : ''
              }`}
            >
              {/* Provider Info */}
              <div className="border-r-2 border-black dark:border-gray-400 flex flex-col justify-center items-center w-48 p-4 flex-shrink-0 text-black dark:text-white transition-colors duration-300">
                <div className="pixel-border w-20 h-20 flex items-center justify-center border-2 border-black dark:border-gray-400 mb-3 bg-white dark:bg-mystic-700 transition-colors duration-300">
                  <FontAwesomeIcon icon={bid.icon} className="text-3xl text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-center mb-2 text-base font-bold">{bid.providerName}</h3>
                <div className="flex items-center w-full mb-1">
                  <span className="mr-2 text-xs font-bold">成功率:</span>
                  <div className="flex-1 border border-black dark:border-gray-400 h-4 bg-white dark:bg-mystic-700 transition-colors duration-300">
                    <div 
                      className="h-full bg-black dark:bg-gray-300 border-r border-black dark:border-gray-400 transition-all duration-300"
                      style={{ width: `${bid.successRate}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs">{bid.successRate}%</span>
                </div>
              </div>

              {/* Service Details */}
              <div className="flex-1 p-4 min-w-0 text-black dark:text-white transition-colors duration-300">
                <div className="flex justify-between mb-4">
                  <h4 className="text-sm font-bold uppercase">服务细节</h4>
                  <div className="flex items-center">
                    <span className="mr-2 text-sm font-bold">响应时间:</span>
                    <span className="text-sm">{bid.responseTime}</span>
                  </div>
                </div>
                
                {/* Scrollable Services List */}
                <div className="border border-black dark:border-gray-400 mb-4 p-3 bg-gray-50 dark:bg-mystic-700 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 transition-colors duration-300">
                  <ul className="text-sm text-black dark:text-white">
                    {bid.services.map((service, index) => (
                      <li key={index} className="mb-2 last:mb-0 leading-relaxed">• {service}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex justify-between items-end">
                  <div></div>
                  <div className="">
                    <div className="flex items-center mb-2">
                      <span className="mr-2 text-sm font-bold">报价:</span>
                      <span className="text-lg font-bold">¥{bid.price}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Button */}
              <div className="border-l-2 border-black dark:border-gray-400 flex items-center w-36 p-4 flex-shrink-0">
                <button
                  onClick={() => handleSelectBid(bid.id)}
                  className={`w-full h-12 text-sm font-bold uppercase transition-all duration-200 border-2 border-black dark:border-gray-400 ${
                    selectedBid === bid.id ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white dark:bg-mystic-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-mystic-600 transition-colors duration-300'
                  }`}
                >
                  {selectedBid === bid.id ? '已选择' : '选择方案'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Confirm Selection Button */}
        <div className="flex justify-center w-full mt-8 mb-12">
          <button
            onClick={handleConfirmSelection}
            disabled={!selectedBid}
            className={`flex items-center w-42 h-15 px-6 text-base font-bold uppercase transition-all duration-200 border-2 border-black dark:border-gray-400 bg-black dark:bg-mystic-700 text-white hover:shadow-pixel-hover ${
              !selectedBid ? 'opacity-50 cursor-not-allowed' : 'hover:transform hover:-translate-y-1 hover:-translate-x-1'
            }`}
          >
            <span>确认方案</span>
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </button>
        </div>
      </main>
    </div>
  );
};

export default BiddingDisplaySoftware; 